// The ArcLog OS Terminal.
//
// This is an ORDINARY SHELL, not a chat client. There is deliberately no
// fallthrough to an LLM: an unrecognised command prints "command not found"
// and stops. Talking to Hermes is the Hermes app's job (`hermes` here just
// launches it), so a typo can never cost money or leak a line of input to a
// model.
//
// Every filesystem command routes through resolvePath() so navigation and
// listing can never disagree about what `../..` means, and every one of them
// stats before acting so "missing" and "wrong type" stay distinguishable.

import { useEffect, useRef, useState } from "react";
import { osStore } from "../../os/store";
import { resolvePath, displayPath } from "../../os/shell-path.mjs";
import { parseCommand } from "../../os/shell-args.mjs";
import {
  completionTarget,
  completeToken,
  splitPathToken,
} from "../../os/shell-complete.mjs";
import { formatLongListing } from "../../os/shell-format.mjs";
import {
  killToLineStart,
  killToLineEnd,
  killWordLeft,
  historyAt,
} from "../../os/shell-readline.mjs";
import type * as OsApi from "../../os/api";

type ShellCtx = ReturnType<typeof OsApi.createModuleContext>;

type Line = { kind: string; text: string };

interface ShellEnv {
  ctx: ShellCtx;
  /** Absolute cwd at the moment this command started. */
  cwd: string;
  /** Where `cd -` goes back to. */
  prevCwd: string;
  home: string;
  chdir: (next: string) => void;
  out: (text: string) => void;
  err: (text: string) => void;
  clear: () => void;
}

type Parsed = ReturnType<typeof parseCommand>;

interface CommandSpec {
  usage: string;
  summary: string;
  /** What Tab completes for this command's arguments. Omitted = nothing. */
  complete?: "path" | "app";
  run: (env: ShellEnv, p: Parsed) => Promise<void> | void;
}

/** Trailing-slash marker so `ls` output distinguishes dirs at a glance. */
function formatEntry(e: { name: string; type: string }): string {
  return e.type === "dir" ? `${e.name}/` : e.name;
}

/** What `ls` and `stat` hand back. Named because ctx.fs.list infers as any[]. */
type FsEntry = { name: string; type: string; size?: number; mtime?: string };

/** Final path component — the name a file keeps when moved into a directory. */
function basename(p: string): string {
  return p.split("/").filter(Boolean).pop() || "";
}

/**
 * Shared body of `mv` and `cp`.
 *
 * Both verbs have exactly one subtle rule and it is the same rule, so it is
 * written once: **an existing destination directory is moved/copied INTO, not
 * overwritten**. `mv a.txt docs/` has to land at `docs/a.txt`.
 *
 * The destination is resolved to a full path *here* rather than left to the
 * server, because the two backing calls disagree about collisions — `fsMove`
 * throws `destination exists` while `fsCopy` silently invents a free name like
 * "a 2.txt". Neither is what a shell user typing `cp` expects, and a copy that
 * lands somewhere other than where you aimed it is worse than a refusal. So
 * the shell decides: refuse unless `-f`, and with `-f` delete the destination
 * first so the write is unambiguous.
 */
async function moveOrCopy(
  env: ShellEnv,
  p: Parsed,
  verb: "mv" | "cp"
): Promise<void> {
  if (p.args.length < 2) return env.err(`usage: ${verb} [-f] <src> <dest>`);
  if (p.args.length > 2) {
    // Multi-source (`mv a b dir/`) isn't supported; failing loudly beats
    // silently ignoring the extra operands and moving only the first.
    return env.err(`${verb}: too many arguments (one source, one destination)`);
  }
  const src = resolvePath(env.cwd, p.args[0], env.home);
  const destArg = resolvePath(env.cwd, p.args[1], env.home);

  if (src === "/") return env.err(`${verb}: /: refusing to ${verb} the filesystem root`);
  if (env.home && src === env.home) {
    return env.err(`${verb}: ${src}: refusing to ${verb} your home directory`);
  }

  const srcStat: FsEntry | null = await env.ctx.fs.stat(src);
  if (!srcStat) return env.err(`${verb}: ${src}: no such file or directory`);

  // `mv` of the directory you're standing in (or an ancestor of it) would
  // leave the shell with a cwd that no longer exists. `cp` is harmless.
  if (verb === "mv" && (src === env.cwd || env.cwd.startsWith(src + "/"))) {
    return env.err(`mv: ${src}: refusing to move the current directory`);
  }

  const destStat: FsEntry | null = await env.ctx.fs.stat(destArg);
  const dest =
    destStat?.type === "dir"
      ? resolvePath(destArg, basename(src), env.home)
      : destArg;

  if (dest === src) return env.err(`${verb}: ${src} and ${dest} are the same file`);
  if (srcStat.type === "dir" && dest.startsWith(src + "/")) {
    return env.err(`${verb}: cannot ${verb} ${src} into itself`);
  }

  // Re-stat: `dest` may have grown a basename since destStat was taken.
  const finalStat: FsEntry | null =
    dest === destArg ? destStat : await env.ctx.fs.stat(dest);
  if (finalStat) {
    if (finalStat.type === "dir") {
      // Only reachable for `mv a docs/` where docs/a is itself a directory.
      return env.err(`${verb}: ${dest}: is a directory`);
    }
    if (!p.flags.f) return env.err(`${verb}: ${dest}: file exists (use -f to overwrite)`);
    await env.ctx.fs.remove(dest);
  }

  if (verb === "mv") await env.ctx.fs.move(src, dest);
  else await env.ctx.fs.copy(src, dest);
}

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${osStore.token}` };
}

// ---------------------------------------------------------------------------
// Command history persistence
// ---------------------------------------------------------------------------

const HISTORY_KEY = "arclog.terminal.history";
/** Enough to scroll back through a session; small enough to stay cheap. */
const HISTORY_MAX = 200;

/**
 * History from the previous session.
 *
 * Defensive on every axis — a hand-edited or half-written localStorage value
 * must not take the terminal down on mount. Anything that isn't an array of
 * non-empty strings degrades to "no history", which is merely the state of a
 * fresh install.
 */
function loadHistory(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(HISTORY_KEY) || "null");
    if (!Array.isArray(raw)) return [];
    return raw.filter((v): v is string => typeof v === "string" && v !== "").slice(-HISTORY_MAX);
  } catch {
    return [];
  }
}

function saveHistory(entries: string[]): void {
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(entries.slice(-HISTORY_MAX)));
  } catch {
    /* private mode / quota — history is a convenience, never a hard failure */
  }
}

function jsonHeaders(): Record<string, string> {
  return { ...authHeaders(), "Content-Type": "application/json" };
}

/** Every installed package whose manifest declares kind:"app". */
async function listApps(ctx: ShellCtx) {
  const list = await ctx.packages.list();
  return list.filter((r) => r.manifest?.kind === "app");
}

function appLabel(r: { manifest: { dock?: { label: string }; name: string } }): string {
  return r.manifest.dock?.label || r.manifest.name;
}

// ---------------------------------------------------------------------------
// The command table. `help` is generated from it, so the two cannot drift.
// ---------------------------------------------------------------------------

const COMMANDS: Record<string, CommandSpec> = {
  help: {
    usage: "help",
    summary: "list the commands you can run",
    run: (env) => {
      const width = Math.max(...Object.values(COMMANDS).map((c) => c.usage.length));
      env.out(
        Object.keys(COMMANDS)
          .map((name) => {
            const c = COMMANDS[name];
            return `${c.usage.padEnd(width)}  ${c.summary}`;
          })
          .join("\n")
      );
    },
  },

  clear: {
    usage: "clear",
    summary: "clear the screen",
    run: (env) => env.clear(),
  },

  pwd: {
    usage: "pwd",
    summary: "print the working directory",
    run: (env) => env.out(env.cwd),
  },

  ls: {
    usage: "ls [-l] [-a] [path]",
    complete: "path",
    summary: "list a directory (-l long format, -a include dot-files)",
    run: async (env, p) => {
      const target = resolvePath(env.cwd, p.args[0], env.home);
      const st: FsEntry | null = await env.ctx.fs.stat(target);
      if (!st) return env.err(`ls: ${target}: no such file or directory`);
      // A path that isn't a directory lists as itself, the way `ls file` does.
      if (st.type !== "dir") {
        return env.out(p.flags.l ? formatLongListing([{ ...st, name: target }])[0] : target);
      }
      // Dot-files are filtered server-side unless asked for, so -a is a
      // listing option rather than a filter applied to what came back.
      const entries: FsEntry[] = await env.ctx.fs.list(target, { showHidden: !!p.flags.a });
      const sorted = [...entries].sort((a, b) => a.name.localeCompare(b.name));
      const body = (p.flags.l ? formatLongListing(sorted) : sorted.map(formatEntry)).join("\n");
      env.out(body || "(empty)");
    },
  },

  cd: {
    usage: "cd [path|-]",
    complete: "path",
    summary: "change directory (no argument goes home, - goes back)",
    run: async (env, p) => {
      const raw = p.args[0];
      const target =
        raw === "-" ? env.prevCwd : resolvePath(env.cwd, raw ?? env.home, env.home);
      const st = await env.ctx.fs.stat(target);
      if (!st) return env.err(`cd: ${target}: no such file or directory`);
      if (st.type !== "dir") return env.err(`cd: ${target}: not a directory`);
      env.chdir(target);
      if (raw === "-") env.out(target);
    },
  },

  cat: {
    usage: "cat <file>",
    complete: "path",
    summary: "print a file",
    run: async (env, p) => {
      if (!p.args[0]) return env.err("usage: cat <file>");
      const target = resolvePath(env.cwd, p.args[0], env.home);
      const st = await env.ctx.fs.stat(target);
      if (!st) return env.err(`cat: ${target}: no such file or directory`);
      if (st.type === "dir") return env.err(`cat: ${target}: is a directory`);
      env.out(await env.ctx.fs.read(target));
    },
  },

  mkdir: {
    usage: "mkdir [-p] <dir>",
    complete: "path",
    summary: "create a directory (-p makes parents and ignores an existing dir)",
    run: async (env, p) => {
      if (!p.args[0]) return env.err("usage: mkdir [-p] <dir>");
      const target = resolvePath(env.cwd, p.args[0], env.home);
      const st = await env.ctx.fs.stat(target);
      if (st) {
        if (p.flags.p) return;
        return env.err(`mkdir: ${target}: file exists`);
      }
      if (!p.flags.p) {
        // The backing mkdir is always recursive, so -p has to be enforced here
        // or `mkdir a/b/c` would quietly succeed without it.
        const parent = resolvePath(target, "..", env.home);
        const pst = parent === target ? null : await env.ctx.fs.stat(parent);
        if (!pst) return env.err(`mkdir: ${target}: no such file or directory`);
        if (pst.type !== "dir") return env.err(`mkdir: ${parent}: not a directory`);
      }
      await env.ctx.fs.mkdir(target);
    },
  },

  touch: {
    usage: "touch <file>",
    complete: "path",
    summary: "create an empty file (never truncates an existing one)",
    run: async (env, p) => {
      if (!p.args[0]) return env.err("usage: touch <file>");
      const target = resolvePath(env.cwd, p.args[0], env.home);
      const st = await env.ctx.fs.stat(target);
      // fs.write is a full overwrite, so an existing path must short-circuit.
      if (st) {
        if (st.type === "dir") return env.err(`touch: ${target}: is a directory`);
        return;
      }
      await env.ctx.fs.write(target, "");
    },
  },

  rm: {
    usage: "rm [-r] <path>",
    complete: "path",
    summary: "delete a file, or a directory with -r (permanent, no undo)",
    run: async (env, p) => {
      if (!p.args[0]) return env.err("usage: rm [-r] <path>");
      const target = resolvePath(env.cwd, p.args[0], env.home);
      // Guard rails first: this deletes from the user's real VFS and the
      // backing store removes directories recursively with no confirmation.
      if (target === "/") return env.err("rm: /: refusing to remove the filesystem root");
      if (env.home && target === env.home) {
        return env.err(`rm: ${target}: refusing to remove your home directory`);
      }
      if (target === env.cwd || env.cwd.startsWith(target + "/")) {
        return env.err(`rm: ${target}: refusing to remove the current directory`);
      }
      const st = await env.ctx.fs.stat(target);
      if (!st) return env.err(`rm: ${target}: no such file or directory`);
      if (st.type === "dir" && !p.flags.r) {
        return env.err(`rm: ${target}: is a directory (use -r)`);
      }
      await env.ctx.fs.remove(target);
    },
  },

  mv: {
    usage: "mv [-f] <src> <dest>",
    complete: "path",
    summary: "move or rename (into dest if dest is a directory; -f overwrites)",
    run: (env, p) => moveOrCopy(env, p, "mv"),
  },

  cp: {
    usage: "cp [-f] <src> <dest>",
    complete: "path",
    summary: "copy a file or directory (into dest if dest is a directory; -f overwrites)",
    run: (env, p) => moveOrCopy(env, p, "cp"),
  },

  echo: {
    usage: "echo <text> [> file | >> file]",
    summary: "print text, or write/append it to a file",
    run: async (env, p) => {
      const text = p.args.join(" ");
      if (!p.redirect) return env.out(text);
      const target = resolvePath(env.cwd, p.redirect.target, env.home);
      const st = await env.ctx.fs.stat(target);
      if (st && st.type === "dir") return env.err(`echo: ${target}: is a directory`);
      // fs.write is a full overwrite, so >> has to read-append-write.
      const prior = p.redirect.mode === "append" && st ? await env.ctx.fs.read(target) : "";
      await env.ctx.fs.write(target, prior + text + "\n");
    },
  },

  apps: {
    usage: "apps",
    summary: "list installed apps",
    run: async (env) => {
      const apps = await listApps(env.ctx);
      const width = Math.max(1, ...apps.map((r) => r.manifest.id.length));
      const body = apps
        .map((r) => `${r.manifest.id.padEnd(width)}  ${appLabel(r)}`)
        .sort()
        .join("\n");
      env.out(body || "(no apps installed)");
    },
  },

  edit: {
    usage: "edit <file>",
    complete: "path",
    summary: "open a file in whichever app handles its type",
    run: async (env, p) => {
      if (!p.args[0]) return env.err("usage: edit <file>");
      const target = resolvePath(env.cwd, p.args[0], env.home);
      const st = await env.ctx.fs.stat(target);
      if (!st) return env.err(`edit: ${target}: no such file or directory`);
      if (st.type === "dir") {
        return env.err(`edit: ${target}: is a directory (try: open ${p.args[0]})`);
      }
      const res = env.ctx.shell.openPath(target);
      if (!res.opened) {
        // Name the thing that failed to route. "nothing happened" is the worst
        // possible answer, and the two causes need different fixes.
        if (res.appId) return env.err(`edit: ${res.appId} is not available for your account`);
        return env.err(
          res.extension
            ? `edit: no app handles ${res.extension}`
            : `edit: ${target}: no file type to open (no extension)`
        );
      }
      env.out(`opening ${target} in ${res.appId}`);
    },
  },

  open: {
    usage: "open <name|id|dir>",
    complete: "app",
    summary: "launch an app by id or name (or reveal a directory in Files)",
    run: async (env, p) => {
      const name = p.args.join(" ").trim();
      if (!name) return env.err("usage: open <name|id|dir>");
      // `open .` / `open ~/Documents` — a directory reveals in Files rather
      // than being hunted for in the app list, where it could never match.
      // Only path-shaped arguments are probed, so an app named "notepad" is
      // never stat()ed against the cwd.
      if (name === "." || name === ".." || name === "~" || /^[.~/]/.test(name)) {
        const target = resolvePath(env.cwd, name, env.home);
        const st = await env.ctx.fs.stat(target);
        if (!st) return env.err(`open: ${target}: no such file or directory`);
        if (st.type === "dir") {
          env.ctx.windows.open("app.files", { params: { path: target } });
          return env.out(`opening ${target} in app.files`);
        }
        // A path-shaped argument that is a file is an `edit`, not an app name.
        const res = env.ctx.shell.openPath(target);
        if (!res.opened) {
          return env.err(
            res.extension
              ? `open: no app handles ${res.extension}`
              : `open: ${target}: no file type to open (no extension)`
          );
        }
        return env.out(`opening ${target} in ${res.appId}`);
      }
      const apps = await listApps(env.ctx);
      const exact = apps.find((r) => r.manifest.id === name || r.id === name);
      const matches = exact
        ? [exact]
        : apps.filter(
            (r) =>
              appLabel(r).toLowerCase() === name.toLowerCase() ||
              r.manifest.name.toLowerCase() === name.toLowerCase()
          );
      if (matches.length === 0) return env.err(`open: ${name}: no such app`);
      if (matches.length > 1) {
        return env.err(
          `open: ${name}: ambiguous — candidates:\n` +
            matches.map((r) => `  ${r.manifest.id}  ${appLabel(r)}`).join("\n")
        );
      }
      const id = matches[0].manifest.id;
      env.ctx.windows.open(id);
      env.out(`opening ${id}`);
    },
  },

  hermes: {
    usage: "hermes",
    summary: "open the Hermes app (this shell never talks to a model itself)",
    run: async (env) => {
      const apps = await listApps(env.ctx);
      if (!apps.some((r) => r.manifest.id === "app.hermes")) {
        return env.err("hermes: Hermes app is not installed");
      }
      env.ctx.windows.open("app.hermes");
      env.out("opening app.hermes");
    },
  },

  exit: {
    usage: "exit",
    summary: "close this terminal window",
    run: (env) => {
      const id = env.ctx.windows.currentId;
      // Only absent if the shell was mounted without a window (it isn't today,
      // but say so rather than appearing to ignore the command).
      if (!id) return env.err("exit: no window to close");
      // Routes through requestCloseWindow, so a close guard still gets its say.
      env.ctx.windows.close(id);
    },
  },

  // ---- OS admin surface (kept from the old terminal) ----------------------

  app: {
    usage: "app list|scaffold|build|publish",
    summary: "App Dev workspace: scaffold, build and publish modules",
    run: async (env, p) => {
      const sub = p.args[0];
      if (sub === "list") {
        const r = await fetch("/api/appdev/workspace", { headers: authHeaders() });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "list failed");
        env.out((d.apps || []).join("\n") || "(no workspace apps)");
      } else if (sub === "scaffold") {
        const id = p.args[1];
        if (!id) throw new Error("usage: app scaffold <id> [Name words…]");
        const name = p.args.slice(2).join(" ") || id;
        const r = await fetch("/api/appdev/scaffold", {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ id, name }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "scaffold failed");
        env.out(
          `scaffolded ${d.moduleId}\n  path: ${d.path}\n  next: app build ${d.moduleId} · app publish ${d.moduleId} 0.1.0 dev`
        );
      } else if (sub === "build") {
        const id = p.args[1];
        if (!id) throw new Error("usage: app build <moduleId>");
        const r = await fetch("/api/appdev/build", {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ moduleId: id.startsWith("app.") ? id : `app.${id}` }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "build failed");
        if (d.ok) env.out(d.logs);
        else env.err(`build failed: ${d.logs}`);
      } else if (sub === "publish") {
        const idRaw = p.args[1];
        if (!idRaw) throw new Error("usage: app publish <moduleId> [version] [channel]");
        const moduleId = idRaw.startsWith("app.") ? idRaw : `app.${idRaw}`;
        const version = p.args[2] || "0.1.0";
        const channel = p.args[3] || "dev";
        const r = await fetch("/api/appdev/publish", {
          method: "POST",
          headers: jsonHeaders(),
          body: JSON.stringify({ moduleId, version, channel }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "publish failed");
        const msg = `published ${d.manifest?.id}@${d.manifest?.version} channel=${d.manifest?.channel}`;
        osStore.notify(msg, "success");
        const { fetchPackages } = await import("../../os/api");
        await fetchPackages();
        env.out(msg);
      } else {
        env.out("app list|scaffold|build|publish");
      }
    },
  },

  os: {
    usage: "os version|update …",
    summary: "OS version and update channel",
    run: async (env, p) => {
      const sub = p.args[0];
      const auth = authHeaders();
      if (sub === "version") {
        const r = await fetch("/api/os/update/version", { headers: auth });
        const d = await r.json();
        env.out(`ArcLog OS ${d.current} (sdk marker ${d.sdk})`);
      } else if (sub === "update") {
        const act = p.args[1];
        if (act === "check") {
          const r = await fetch("/api/os/update/check", { headers: auth });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || "check failed");
          const body = (d.updates || [])
            .map(
              (u: Record<string, string>) =>
                `${u.id}  ${u.fromVersion || "*"} → ${u.toVersion}  [${u.channel}]  ${u.notes || ""}`
            )
            .join("\n");
          env.out(body || "(no updates in catalog)");
        } else if (act === "download" || act === "stage" || act === "apply") {
          const id = p.args[2];
          if (!id) throw new Error(`usage: os update ${act} <id>`);
          const r = await fetch(`/api/os/update/${act}`, {
            method: "POST",
            headers: jsonHeaders(),
            body: JSON.stringify({ id }),
          });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || `${act} failed`);
          env.out(
            `${act} ${d.manifest?.id || id} → status=${d.status}\n${d.manifest?.notes || ""}`.trim()
          );
        } else if (act === "status" || !act) {
          const r = await fetch("/api/os/update/status", { headers: auth });
          const d = await r.json();
          if (!r.ok) throw new Error(d.error || "status failed");
          const body = (d.updates || [])
            .map(
              (u: { manifest?: Record<string, string>; status: string }) =>
                `${u.manifest?.id}  ${u.status}  →${u.manifest?.toVersion}`
            )
            .join("\n");
          env.out(body || "(no update activity)");
        } else {
          env.out("os update check|download|stage|apply|status");
        }
      } else {
        env.out("os version | os update …");
      }
    },
  },

  pkg: {
    usage: "pkg list|install|versions|use|uninstall|info",
    summary: "package manager",
    run: async (env, p) => {
      const ctx = env.ctx;
      const sub = p.args[0];
      if (sub === "list") {
        const list = await ctx.packages.list();
        const body = list
          .map((r) => {
            const vers = r.versions
              ? Object.keys(r.versions)
                  .map((v) => (v === r.defaultVersion ? `${v}*` : v))
                  .join(",")
              : r.manifest.version;
            return `${r.manifest.id}  [${r.state}]  v:${vers}${r.manifest.core ? "  (core)" : ""}`;
          })
          .join("\n");
        env.out(body || "(none)");
      } else if (sub === "install") {
        const name = p.args[1] || "hello-demo";
        const version = p.args[2];
        const channel = p.args[3] || (version ? "beta" : "stable");
        const rec = version
          ? await ctx.packages.install({ name, version, channel })
          : await ctx.packages.install({ name });
        const msg = `installed ${rec.manifest.id}@${rec.manifest.version} (default ${rec.defaultVersion}) channel=${rec.manifest.channel || "stable"}`;
        ctx.notify.push(msg, "success");
        env.out(msg);
      } else if (sub === "versions") {
        const id = p.args[1];
        if (!id) throw new Error("usage: pkg versions <id>");
        const versions = await ctx.packages.listVersions(id);
        const body = versions
          .map((v) => `${v.version}  ${v.channel}  ${v.state}`)
          .join("\n");
        env.out(body || "(none)");
      } else if (sub === "use") {
        const id = p.args[1];
        const version = p.args[2];
        if (!id || !version) throw new Error("usage: pkg use <id> <version>");
        const rec = await ctx.packages.setDefaultVersion(id, version);
        env.out(`default ${id} → ${rec.defaultVersion}`);
      } else if (sub === "uninstall") {
        const id = p.args[1];
        const version = p.args[2];
        if (!id) throw new Error("usage: pkg uninstall <id> [version]");
        await ctx.packages.uninstall(id, version);
        ctx.notify.push(version ? `Uninstalled ${id}@${version}` : `Uninstalled ${id}`, "success");
        env.out(version ? `uninstalled ${id}@${version}` : `uninstalled ${id}`);
      } else if (sub === "info") {
        const list = await ctx.packages.list();
        const rec = list.find((r) => r.manifest.id === p.args[1] || r.id === p.args[1]);
        env.out(
          rec
            ? JSON.stringify(
                {
                  id: rec.id || rec.manifest.id,
                  defaultVersion: rec.defaultVersion,
                  versions: rec.versions ? Object.keys(rec.versions) : [rec.manifest.version],
                  manifest: rec.manifest,
                },
                null,
                2
              )
            : "not found"
        );
      } else {
        env.out("pkg list|install|versions|use|uninstall|info");
      }
    },
  },
};

export function TerminalApp({ windowId }: { windowId?: string }) {
  const [lines, setLines] = useState<Line[]>([
    { kind: "sys", text: "ArcLog OS Terminal — a shell. Type 'help' for commands." },
    { kind: "sys", text: "This shell never talks to a model. Run 'hermes' for that." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cwd, setCwd] = useState("/");
  const [home, setHome] = useState("");
  // cwd/prevCwd also live in refs: a command runs async, and the next command
  // must see the directory the previous one changed to, not a stale render.
  const cwdRef = useRef("/");
  const prevCwdRef = useRef("/");
  const homeRef = useRef("");
  const inputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // Only follow new output when the user is already at the bottom — otherwise
  // scrolling up to read earlier output would yank you back down every time a
  // command prints a line.
  const pinnedRef = useRef(true);
  // History lives in a ref, not state: nothing renders from it, and the
  // keydown handler must see the entry a just-submitted command added rather
  // than a value captured on the previous render.
  const historyRef = useRef<string[]>([]);
  /** Cursor into history. `=== historyRef.current.length` means "the draft". */
  const histIndexRef = useRef(0);
  /** The half-typed line stashed when Up first left the draft position. */
  const draftRef = useRef("");
  /**
   * Caret to apply after the next render. An <input> keeps its own caret when
   * React swaps `value`, so a programmatic edit has to place it explicitly —
   * otherwise Ctrl+U leaves the cursor at the old column.
   */
  const caretRef = useRef<number | null>(null);
  /**
   * The input a Tab was last pressed against, or null.
   *
   * Bash lists completions only on a *second* consecutive Tab, so this records
   * what the previous Tab applied to. It must track the input too, not just
   * "was the last key Tab": Tab, edit, Tab is two first-presses, and treating
   * it as a streak would dump candidates the user never asked for.
   */
  const tabArmedRef = useRef<string | null>(null);
  const ctxPromise = useState(() =>
    import("../../os/api").then((m) => m.createModuleContext({ windowId }))
  )[0];

  useEffect(() => {
    let alive = true;
    void (async () => {
      const ctx = await ctxPromise;
      if (!alive) return;
      const me = ctx.user.me();
      const start = me?.homePath || "/";
      cwdRef.current = start;
      prevCwdRef.current = start;
      homeRef.current = me?.homePath || "";
      setCwd(start);
      setHome(homeRef.current);
    })();
    return () => {
      alive = false;
    };
  }, [ctxPromise]);

  // Restore history once, on mount.
  useEffect(() => {
    historyRef.current = loadHistory();
    histIndexRef.current = historyRef.current.length;
  }, []);

  // Follow output as it appears. Runs after the DOM has the new lines, so
  // scrollHeight is already correct.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [lines]);

  // Place a pending caret after the DOM has the new value.
  useEffect(() => {
    const want = caretRef.current;
    caretRef.current = null;
    if (want == null) return;
    const el = inputRef.current;
    if (!el) return;
    const at = Math.max(0, Math.min(el.value.length, want));
    el.setSelectionRange(at, at);
  }, [input]);

  const focusInput = () => inputRef.current?.focus();

  /** Apply a pure line edit: text now, caret after the render that shows it. */
  const applyEdit = (edit: { text: string; caret: number }) => {
    setInput(edit.text);
    caretRef.current = edit.caret;
  };

  /** Move the caret without touching the text (Ctrl+A / Ctrl+E). */
  const setCaret = (at: number) => {
    const el = inputRef.current;
    if (!el) return;
    const n = Math.max(0, Math.min(el.value.length, at));
    el.setSelectionRange(n, n);
  };

  /** Put a history line on the prompt, caret at the end like a real shell. */
  const recall = (index: number) => {
    const hist = historyRef.current;
    histIndexRef.current = index;
    const text = index >= hist.length ? draftRef.current : historyAt(hist, index);
    applyEdit({ text, caret: text.length });
  };

  /**
   * Record a submitted command. Blank lines are never kept, and a command
   * identical to the one before it is not duplicated — repeating a command is
   * the single most common thing a user does, and storing each repeat would
   * make Up walk through N copies of it before reaching anything else.
   */
  const remember = (text: string) => {
    const hist = historyRef.current;
    if (text && hist[hist.length - 1] !== text) {
      hist.push(text);
      if (hist.length > HISTORY_MAX) hist.splice(0, hist.length - HISTORY_MAX);
      saveHistory(hist);
    }
    histIndexRef.current = hist.length;
    draftRef.current = "";
  };

  /**
   * Tab completion. The algorithm lives in shell-complete.mjs (pure, tested);
   * this only sources the candidates, which is the part that needs the fs and
   * the package list.
   *
   * `listStalled` is bash's double-Tab rule. When completion cannot extend the
   * token any further there is nothing to do on the first press, and only a
   * second consecutive Tab prints the candidates. Listing on the first press
   * meant every ambiguous Tab dumped a wall of output the user hadn't asked
   * for and couldn't take back — and it made Tab-to-extend feel unpredictable,
   * because you couldn't tell a stall from a completion without reading the
   * screen. An extension still happens on the first Tab, as before.
   */
  const complete = async (line: string, listStalled: boolean) => {
    const { isFirstWord, token, index } = completionTarget(line);
    let candidates: string[] = [];
    let suffixDir = "";

    if (isFirstWord) {
      candidates = Object.keys(COMMANDS);
    } else {
      const cmd = parseCommand(line).cmd;
      const kind = cmd ? COMMANDS[cmd]?.complete : undefined;
      if (!kind) return; // nothing sensible to complete — leave the line alone
      const ctx = await ctxPromise;
      if (kind === "app") {
        const apps = await listApps(ctx);
        candidates = apps.map((a) => a.manifest.id);
      } else {
        // Split the token so `sub/fi` lists `sub` and matches `fi`, then put the
        // directory part back on the front of whatever we complete to.
        const { dir, base } = splitPathToken(token);
        suffixDir = dir;
        const listAt = resolvePath(cwdRef.current, dir || ".", homeRef.current);
        try {
          // ctx.fs.list is inferred as any[] (createModuleContext has no return
          // annotation), so name the shape here rather than leaning on inference.
          const entries: Array<{ name: string; type: string }> = await ctx.fs.list(listAt);
          candidates = entries
            .filter((e) => e.name.startsWith(base))
            // A trailing slash on directories lets you keep tabbing downward.
            .map((e) => (e.type === "dir" ? `${e.name}/` : e.name));
        } catch {
          return; // unreadable directory — completing is not worth an error line
        }
        const r = completeToken(base, candidates);
        if (r.suggestions.length && listStalled) {
          setLines((p) => [...p, { kind: "sys", text: r.suggestions.join("  ") }]);
        }
        if (r.value !== base) setInput(line.slice(0, index) + suffixDir + r.value);
        return;
      }
    }

    const r = completeToken(token, candidates);
    if (r.suggestions.length && listStalled) {
      setLines((p) => [...p, { kind: "sys", text: r.suggestions.join("  ") }]);
    }
    if (r.value !== token) setInput(line.slice(0, index) + r.value);
  };

  const run = async (raw: string) => {
    const text = raw.trim();
    if (!text) return;
    setInput("");
    remember(text);
    const prompt = `${displayPath(cwdRef.current, homeRef.current)} ›`;
    setLines((p) => [...p, { kind: "user", text: `${prompt} ${text}` }]);

    const parsed = parseCommand(text);
    if (parsed.error) {
      setLines((p) => [...p, { kind: "err", text: parsed.error as string }]);
      return;
    }
    if (!parsed.cmd) return;

    const spec = COMMANDS[parsed.cmd];
    if (!spec) {
      setLines((p) => [
        ...p,
        { kind: "err", text: `${parsed.cmd}: command not found` },
        { kind: "sys", text: "try 'help'" },
      ]);
      return;
    }

    // The command runner owns `busy` — nothing else does, now that the shell
    // has no streaming agent subscription to hand it off to.
    setBusy(true);
    try {
      const ctx = await ctxPromise;
      const env: ShellEnv = {
        ctx,
        cwd: cwdRef.current,
        prevCwd: prevCwdRef.current,
        home: homeRef.current,
        chdir: (next) => {
          prevCwdRef.current = cwdRef.current;
          cwdRef.current = next;
          setCwd(next);
        },
        out: (t) => setLines((p) => [...p, { kind: "sys", text: t }]),
        err: (t) => setLines((p) => [...p, { kind: "err", text: t }]),
        clear: () => setLines([]),
      };
      await spec.run(env, parsed);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setLines((p) => [...p, { kind: "err", text: `${parsed.cmd}: ${msg}` }]);
    } finally {
      setBusy(false);
      // Re-assert focus after the command settles. The input is never disabled
      // (see below), but a command may have opened a window or shown a dialog.
      focusInput();
    }
  };

  const prompt = `${displayPath(cwd, home)} ›`;

  return (
    // Clicking anywhere in the terminal focuses the input, the way a real one
    // behaves — and it recovers focus lost to anything outside our control.
    // mousedown, not click, so a text selection isn't stolen mid-drag.
    <div
      className="term"
      onMouseDown={(e) => {
        if (window.getSelection()?.toString()) return; // let the user select output
        if (e.target !== inputRef.current) {
          e.preventDefault(); // don't blur the input we're about to focus
          focusInput();
        }
      }}
    >
      <div
        className="term-scroll"
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
        }}
      >
        {lines.map((l, i) => (
          <div key={i} className={`term-line ${l.kind}`}>
            {l.text}
          </div>
        ))}
      </div>
      <div className="term-input-row">
        <span className="prompt">{prompt}</span>
        <input
          ref={inputRef}
          value={input}
          /* NOT `disabled={busy}`: the browser blurs a disabled element, and
             re-enabling does not restore focus — so every Enter dropped focus.
             The input stays enabled (you can type ahead, like a real shell) and
             Enter is ignored while a command is running. */
          autoFocus
          spellCheck={false}
          onChange={(e) => {
            setInput(e.target.value);
            // Typing or pasting breaks a Tab streak, same as bash. Handled
            // here as well as in onKeyDown because a paste fires no keydown.
            tabArmedRef.current = null;
          }}
          onKeyDown={(e) => {
            // Ctrl-chords. `!metaKey` matters on macOS: ⌘A must stay
            // "select all" and ⌘C must stay "copy" — only Ctrl is ours.
            const ctrl = e.ctrlKey && !e.metaKey && !e.altKey;
            const el = e.currentTarget;
            const caret = el.selectionStart ?? input.length;

            if (ctrl) {
              const k = e.key.toLowerCase();
              // These all return early, so disarm Tab up front rather than in
              // seven places. (A ctrl chord that edits the line does not fire
              // onChange — React sets the value programmatically.)
              if ("caeukwld".includes(k)) tabArmedRef.current = null;
              if (k === "d") {
                // EOF closes the shell — but only on an empty line, as in a
                // real terminal. Mid-line it must stay out of the way.
                if (input !== "") return;
                e.preventDefault();
                void (async () => {
                  const ctx = await ctxPromise;
                  const id = ctx.windows.currentId;
                  if (id) ctx.windows.close(id);
                })();
                return;
              }
              if (k === "c") {
                // A selection means the user is copying, not interrupting.
                // Stealing Ctrl+C here would make output impossible to copy.
                if (window.getSelection()?.toString()) return;
                e.preventDefault();
                const p = `${displayPath(cwdRef.current, homeRef.current)} ›`;
                setLines((prev) => [...prev, { kind: "user", text: `${p} ${input}^C` }]);
                setInput("");
                // The abandoned line is NOT recorded — you never ran it — and
                // history itself is untouched, so Up still works afterwards.
                histIndexRef.current = historyRef.current.length;
                draftRef.current = "";
                return;
              }
              if (k === "a") {
                e.preventDefault(); // browser default is Select All
                setCaret(0);
                return;
              }
              if (k === "e") {
                e.preventDefault();
                setCaret(input.length);
                return;
              }
              if (k === "u") {
                e.preventDefault();
                applyEdit(killToLineStart(input, caret));
                return;
              }
              if (k === "k") {
                e.preventDefault();
                applyEdit(killToLineEnd(input, caret));
                return;
              }
              if (k === "w") {
                e.preventDefault();
                applyEdit(killWordLeft(input, caret));
                return;
              }
              if (k === "l") {
                e.preventDefault(); // browser default is focus the address bar
                setLines([]);
                return;
              }
            }

            if (e.key === "Tab") {
              // Always preventDefault: Tab would otherwise move focus out of
              // the terminal entirely, which is the opposite of what's wanted.
              e.preventDefault();
              if (busy) return;
              // Armed means the previous Tab was pressed against this exact
              // input and got nowhere — so this one is the listing press.
              const second = tabArmedRef.current === input;
              tabArmedRef.current = input;
              void complete(input, second);
              return;
            }

            // Any other key ends a Tab streak. Bare modifiers are excluded:
            // holding Shift is not "pressing a different key", and treating it
            // as one would silently cost you the second Tab.
            if (e.key !== "Shift" && e.key !== "Control" && e.key !== "Alt" && e.key !== "Meta") {
              tabArmedRef.current = null;
            }

            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (histIndexRef.current === historyRef.current.length) {
                // Leaving the draft for the first time — stash it so coming
                // back down returns the line you were part-way through.
                draftRef.current = input;
              }
              if (histIndexRef.current > 0) recall(histIndexRef.current - 1);
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (histIndexRef.current < historyRef.current.length) {
                recall(histIndexRef.current + 1);
              }
              return;
            }

            if (e.key !== "Enter") return;
            e.preventDefault();
            if (busy) return;
            void run(input);
          }}
          placeholder={busy ? "working…" : "type a command — 'help' lists them"}
        />
      </div>
    </div>
  );
}
